import { memo, useCallback, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { getRecentTickerUrl } from '../../api/generated/game-punch/game-punch.ts';
import TopProfileImage from '../../assets/icons/punch/top-profile.png';

const TICKER_SPEED_PX_PER_MS = 25 / 1000;
const MOCK_TICKER_INTERVAL_MS = 3000;
const MOCK_TICKER_MAX = 6;
const MOCK_REWARD_POINTS = [100, 150];
const MOCK_REWARD_COLOR_BY_POINT = {
  100: '#DAA520',
  150: '#ff6b9d',
};

const pickRandom = (list) => list[Math.floor(Math.random() * list.length)];

const truncateName = (name, maxLen = 6) => {
  const text = String(name ?? '');
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen)}...`;
};

const createMockTickerItem = (namePool, tickerNoBase) => {
  const rewardPoint = pickRandom(MOCK_REWARD_POINTS);
  return {
    tickerNo: tickerNoBase,
    nickname: pickRandom(namePool) ?? '냥냥이',
    rewardPoint,
    rewardColor: MOCK_REWARD_COLOR_BY_POINT[rewardPoint],
  };
};

function WinnerTickerView({ winners }) {
  // winners는 mock({ name, point }) 또는 API({ nickname, rewardPoint, rewardColor }) 형태 모두 지원
  const normalized = winners.map((w) => ({
    name: w.name ?? w.nickname ?? '',
    point: w.point ?? w.rewardPoint ?? 0,
    rewardColor: w.rewardColor ?? null,
  }));
  const hasRewardColor = normalized.some((w) => typeof w.rewardColor === 'string' && w.rewardColor.length > 0);
  const tickerWinners = hasRewardColor
    ? normalized.slice(-6)
    : normalized.filter((w) => w.point === 100 || w.point === 150).slice(-6);
  const shouldLoopTicker = tickerWinners.length > 0;
  const isSingleTicker = tickerWinners.length === 1;
  const renderedTickerWinners = shouldLoopTicker ? [...tickerWinners, ...tickerWinners] : tickerWinners;

  const trackRef = useRef(null);
  const rafRef = useRef(null);
  const posRef = useRef(0);
  const lastTimeRef = useRef(null);

  useEffect(() => {
    if (!shouldLoopTicker) return undefined;
    lastTimeRef.current = null;

    const animate = (timestamp) => {
      if (lastTimeRef.current === null) lastTimeRef.current = timestamp;
      const delta = Math.min(timestamp - lastTimeRef.current, 100);
      lastTimeRef.current = timestamp;

      const track = trackRef.current;
      if (track) {
        const halfWidth = track.scrollWidth / 2;
        if (halfWidth > 0) {
          posRef.current -= TICKER_SPEED_PX_PER_MS * delta;
          while (posRef.current <= -halfWidth) {
            posRef.current += halfWidth;
          }
          track.style.transform = `translateX(${posRef.current}px) translateZ(0)`;
        }
      }
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [shouldLoopTicker]);

  const getWinnerPointClass = (point) => {
    if (point === 150) return 'text-[#ff6b9d]';
    if (point === 100) return 'text-[#DAA520]';
    return 'text-gray03';
  };

  return (
    <div className="relative z-[5] flex h-12 items-center gap-3 bg-white px-[10px]">
      <img src={TopProfileImage} alt="프로필" className="h-12 w-12 shrink-0 object-cover" />
      <div className="flex-1 overflow-hidden" aria-label="최근 당첨 내역">
        <div ref={trackRef} className={`flex items-center ${isSingleTicker ? 'w-full gap-0' : 'w-max gap-5'}`} style={{ willChange: 'transform' }}>
          {renderedTickerWinners.map((winner, index) => (
            <div
              className={`inline-flex items-center gap-0.5 whitespace-nowrap font-['Pretendard'] text-xs font-extrabold leading-[18px] tracking-[-0.3px] ${
                isSingleTicker ? 'min-w-full' : ''
              }`}
              key={`${winner.name}-${winner.point}-${index}`}
            >
              <span className="text-gray04">{truncateName(winner.name)}</span>
              <span
                className={!winner.rewardColor ? getWinnerPointClass(winner.point) : ''}
                style={winner.rewardColor ? { color: winner.rewardColor } : undefined}
              >
                {winner.point}P
              </span>
              <span className="text-gray04">당첨</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const MemoWinnerTickerView = memo(WinnerTickerView);

export default function PunchWinnerTicker({
  isPunchMockModeEnabled,
  mockWinners,
  accessToken,
  API_SERVER_DOMAIN,
  fetchWithToken,
}) {
  const [stableRecentTickerInfos, setStableRecentTickerInfos] = useState([]);
  const [mockTickerInfos, setMockTickerInfos] = useState([]);
  const mockTickerNoRef = useRef(0);

  const fetchRecentTicker = useCallback(
    async (...args) => {
      try {
        const response = await fetchWithToken(...args);
        return response;
      } catch (error) {
        console.error('backend api: error GET /punch/ticker/recent', { error });
        throw error;
      }
    },
    [fetchWithToken],
  );

  const { data: recentTickerData } = useSWR(
    accessToken && !isPunchMockModeEnabled && [`${API_SERVER_DOMAIN}${getRecentTickerUrl({ limit: 6 })}`, accessToken],
    fetchRecentTicker,
    { refreshInterval: 3000, revalidateOnFocus: false },
  );

  useEffect(() => {
    if (!Array.isArray(recentTickerData?.infos) || recentTickerData.infos.length === 0) return;

    const nextInfos = recentTickerData.infos;
    const nextSignature = nextInfos
      .map((item) => `${item.tickerNo ?? ''}:${item.userNo ?? ''}:${item.nickname ?? ''}:${item.rewardPoint ?? ''}:${item.rewardColor ?? ''}`)
      .join('|');

    setStableRecentTickerInfos((prev) => {
      const prevSignature = prev
        .map((item) => `${item.tickerNo ?? ''}:${item.userNo ?? ''}:${item.nickname ?? ''}:${item.rewardPoint ?? ''}:${item.rewardColor ?? ''}`)
        .join('|');
      return prevSignature === nextSignature ? prev : nextInfos;
    });
  }, [recentTickerData?.infos]);

  useEffect(() => {
    if (!isPunchMockModeEnabled) return undefined;
    const namePool =
      Array.isArray(mockWinners) && mockWinners.length > 0
        ? mockWinners.map((winner) => winner.name ?? winner.nickname).filter(Boolean)
        : ['하은', '유찬', '성준', '도윤', '지안', '예준', '서윤', '민재'];

    if (mockTickerNoRef.current === 0) {
      mockTickerNoRef.current = 1;
    }

    setMockTickerInfos([createMockTickerItem(namePool, mockTickerNoRef.current)]);
    mockTickerNoRef.current += 1;

    const timer = window.setInterval(() => {
      setMockTickerInfos((prev) => {
        if (prev.length < MOCK_TICKER_MAX) {
          const next = [...prev, createMockTickerItem(namePool, mockTickerNoRef.current)];
          mockTickerNoRef.current += 1;
          return next;
        }

        return prev.map(() => {
          const item = createMockTickerItem(namePool, mockTickerNoRef.current);
          mockTickerNoRef.current += 1;
          return item;
        });
      });
    }, MOCK_TICKER_INTERVAL_MS);

    return () => {
      window.clearInterval(timer);
    };
  }, [isPunchMockModeEnabled, mockWinners]);

  const winners = isPunchMockModeEnabled ? mockTickerInfos : stableRecentTickerInfos;

  return <MemoWinnerTickerView winners={winners} />;
}
